import { forwardRef, memo, useEffect, useImperativeHandle, useLayoutEffect, useMemo, useRef } from "react";
import PropTypes from "prop-types";

// Wrapper for a WHEP-based WebRTC live stream.
// Attempts to provide the same interface as ReactPlayer / MpegTsPlayer so it slots
// into VideoPlayer's player selection and ref-based API without callers noticing.
const WebRtcPlayer = memo(forwardRef(({
    url,
    width = "100%",
    height = "100%",
    playing,
    // Swallowed to match MpegTsPlayer - no volume UI yet.
    // eslint-disable-next-line no-unused-vars
    volume,
    // ReactPlayer-only props - kept off the <video> element
    // eslint-disable-next-line no-unused-vars
    onDuration, onProgress, controls,
    muted,
    playsinline,
    playbackRate,
    onReady,
    onPlay,
    onPause,
    onBuffer,
    onBufferEnd,
    onError,
    onLoadedData,
    config,
    ...props
}, ref) => {

    // <video> element used to render the incoming MediaStream
    const videoRef = useRef();
    // RTCPeerConnection instance
    const pcRef = useRef();

    // Stabilise callbacks / options used inside the signalling effect
    // so latest values are used without re-running WHEP handshake
    const onErrorRef = useRef();
    const webrtcOptionsRef = useRef();
    useLayoutEffect(() => {
        onErrorRef.current = onError;
        webrtcOptionsRef.current = config?.webrtc;
    });

    // Ref exposed by this component
    const thisRef = useMemo(() => {
        return {
            isWebRtc: true,
            // Match ReactPlayer / MpegTsPlayer shape
            getInternalPlayer: (key) => key === 'peerConnection' ? pcRef.current : videoRef.current,
            getCurrentTime: () => videoRef.current?.currentTime ?? 0,
            // Live stream has no known duration
            getDuration: () => null,
            getSecondsLoaded: () => null,
            seekTo: () => { /* not supported for live WebRTC */ }
        };
    }, []);

    useImperativeHandle(ref, () => {
        return thisRef;
    }, [thisRef]);

    // Set up WHEP handshake
    useEffect(() => {

        let cancelled = false;
        // URL of the WHEP resource, captured from Location header on the answer response.
        // Used to send DELETE on teardown.
        let resourceLocation = null;
        let audioTimer;
        let cancelIceGathering;
        let errored = false;
        const emitError = (error) => {
            if (errored || cancelled) return;
            errored = true;
            onErrorRef.current?.(error);
        };

        const options = webrtcOptionsRef.current ?? {};
        const doFetch = options.fetch ?? fetch;
        // Headers may be a function so auth tokens are read fresh per request
        const getHeaders = () => (typeof options.headers === 'function' ? options.headers() : options.headers) ?? {};
        const deleteResource = async () => {
            if (!resourceLocation) return;
            const location = resourceLocation;
            resourceLocation = null;
            try {
                await doFetch(location, { method: 'DELETE', headers: getHeaders() });
            } catch (error) { /* best effort */ }
        };

        let pc;
        try {
            pc = new RTCPeerConnection(options.iceServers ? { iceServers: options.iceServers } : undefined);
            pc.addTransceiver('video', { direction: 'recvonly' });
            const audioTransceiver = pc.addTransceiver('audio', { direction: 'recvonly' });

            // Prefer G.711 for MediaMTX pass-through, retaining Opus as a fallback.
            if (typeof RTCRtpReceiver !== 'undefined' && typeof RTCRtpReceiver.getCapabilities === 'function' && audioTransceiver.setCodecPreferences) {
                const audioCaps = RTCRtpReceiver.getCapabilities('audio');
                if (audioCaps?.codecs?.length) {
                    const g711 = [];
                    const rest = [];
                    audioCaps.codecs.forEach(codec => {
                        const mime = codec.mimeType?.toLowerCase();
                        if (mime === 'audio/pcmu' || mime === 'audio/pcma') {
                            g711.push(codec);
                        } else {
                            rest.push(codec);
                        }
                    });
                    if (g711.length) {
                        try {
                            audioTransceiver.setCodecPreferences([...g711, ...rest]);
                        } catch (error) {
                            console.warn('Failed to set audio codec preferences', error);
                        }
                    }
                }
            }
        } catch (error) {
            try { pc?.close(); } catch (closeError) { /* best effort */ }
            emitError(error);
            return;
        }
        pcRef.current = pc;

        // Attach incoming media to the <video> element.
        //
        // Deliberately ignores `event.streams`: the WHEP server (MediaMTX) omits
        // a=msid on the audio m-line, and browsers disagree on what streams[]
        // contains for such non-signaled tracks. Chrome fires the audio track
        // event with an empty streams array, but Firefox synthesizes an
        // audio-only MediaStream for it - and since the audio event fires after
        // the video one, assigning event.streams[0] here replaced the video
        // stream with an audio-only stream, producing a black screen in Firefox
        // (and silently dropped audio in Chrome). Accumulating tracks into our
        // own MediaStream is immune to msid/browser differences.
        pc.ontrack = (event) => {
            const video = videoRef.current;
            if (!video || cancelled) return;
            if (!video.srcObject) {
                video.srcObject = new MediaStream();
            }

            if (event.track.kind !== 'audio') {
                video.srcObject.addTrack(event.track);
                return;
            }

            // MediaMTX can negotiate audio without sending RTP.
            const receiver = event.receiver;
            let attached = false;
            const pollAudio = async () => {
                if (attached || cancelled) return;
                try {
                    const stats = await receiver.getStats();
                    let bytesReceived = 0;
                    stats.forEach(report => {
                        if (report.type === 'inbound-rtp' && report.kind === 'audio') {
                            bytesReceived = report.bytesReceived ?? bytesReceived;
                        }
                    });
                    if (bytesReceived > 0 && !cancelled) {
                        attached = true;
                        if (video.srcObject) {
                            video.srcObject.addTrack(event.track);
                            video.dispatchEvent(new Event('manythingaudiotrackavailable'));
                        }
                        return;
                    }
                } catch (e) { /* ignore transient getStats errors */ }
                if (!cancelled) {
                    audioTimer = setTimeout(pollAudio, 250);
                }
            };
            pollAudio();
        };

        pc.oniceconnectionstatechange = () => {
            // 'disconnected' is a transient state that can recover on its own
            // (Firefox in particular passes through it during normal operation),
            // so only 'failed' is treated as fatal.
            if (pc.iceConnectionState === 'failed') {
                emitError(new Error(`WebRTC ICE state: ${pc.iceConnectionState}`));
            }
        };
        // Catches the server closing the session, which ICE state alone may not report
        pc.onconnectionstatechange = () => {
            if (pc.connectionState === 'failed' || pc.connectionState === 'closed') {
                emitError(new Error(`WebRTC connection state: ${pc.connectionState}`));
            }
        };

        const doWhep = async () => {
            try {
                const offer = await pc.createOffer();
                await pc.setLocalDescription(offer);

                // Non-trickle WHEP: wait for ICE gathering to finish before sending
                // the offer, but cap the wait - if gathering stalls (misbehaving
                // network interface, unreachable STUN server) we proceed with
                // whatever candidates we have rather than hanging forever.
                if (pc.iceGatheringState !== 'complete') {
                    await new Promise((resolve) => {
                        const finish = () => {
                            clearTimeout(timer);
                            pc.removeEventListener('icegatheringstatechange', check);
                            resolve();
                        };
                        const check = () => {
                            if (pc.iceGatheringState === 'complete') finish();
                        };
                        const timer = setTimeout(finish, 2000);
                        cancelIceGathering = finish;
                        pc.addEventListener('icegatheringstatechange', check);
                    });
                }

                if (cancelled) return;

                const response = await doFetch(url, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/sdp',
                        ...getHeaders()
                    },
                    body: pc.localDescription.sdp
                });

                if (!response.ok) {
                    const detail = await response.text().catch(() => '');
                    throw new Error(`WHEP signalling failed: ${response.status} ${response.statusText} ${detail}`.trim());
                }

                const location = response.headers.get('Location');
                if (location) {
                    // Location may be relative to the WHEP endpoint
                    try {
                        resourceLocation = new URL(location, new URL(url, document.baseURI)).toString();
                    } catch (e) {
                        resourceLocation = location;
                    }
                }
                if (cancelled) {
                    await deleteResource();
                    return;
                }

                const answerSdp = await response.text();
                if (cancelled) return;

                await pc.setRemoteDescription({ type: 'answer', sdp: answerSdp });
            } catch (error) {
                emitError(error);
            }
        };

        doWhep();

        // The server ties the session's lifetime to the last token it saw, so hand it each refreshed one
        let sentAuthorization = getHeaders().Authorization;
        const renewInterval = setInterval(() => {
            const headers = getHeaders();
            if (!resourceLocation || headers.Authorization === sentAuthorization) return;
            sentAuthorization = headers.Authorization;
            doFetch(resourceLocation, { method: 'PATCH', headers }).catch(() => { /* session lapses; connection state reports it */ });
        }, 30000);

        const video = videoRef.current;
        const handleError = event => emitError(video.error ?? event);
        video.addEventListener('error', handleError);

        // Fail over if signalling succeeds but no media ever arrives
        const startupTimer = setTimeout(() => emitError(new Error('WebRTC startup timed out')), 10000);
        const clearStartupTimer = () => clearTimeout(startupTimer);
        video.addEventListener('loadeddata', clearStartupTimer);

        // MediaStreams rarely fire 'waiting', so synthesise waiting/playing from decoded frame progress
        let lastFramesDecoded = 0;
        let stalled = false;
        const stallInterval = setInterval(async () => {
            let framesDecoded = 0;
            try {
                (await pc.getStats()).forEach(report => {
                    if (report.type === 'inbound-rtp' && report.kind === 'video') framesDecoded = report.framesDecoded ?? 0;
                });
            } catch (e) { return; }
            if (cancelled || !framesDecoded) return;
            const nowStalled = framesDecoded === lastFramesDecoded;
            lastFramesDecoded = framesDecoded;
            if (nowStalled !== stalled) {
                stalled = nowStalled;
                video.dispatchEvent(new Event(stalled ? 'waiting' : 'playing'));
            }
        }, 2000);

        return () => {
            cancelled = true;
            clearTimeout(startupTimer);
            clearInterval(stallInterval);
            clearInterval(renewInterval);
            video.removeEventListener('loadeddata', clearStartupTimer);
            clearTimeout(audioTimer);
            cancelIceGathering?.();
            video.removeEventListener('error', handleError);
            deleteResource();

            try { video?.srcObject?.getTracks().forEach(track => track.stop()); } catch (e) { /* ignore */ }
            try { pc.close(); } catch (e) { /* ignore */ }
            if (video) {
                video.srcObject = null;
            }
            pcRef.current = null;
        };
    }, [url]);

    // Bridge native <video> events to ReactPlayer-style callbacks
    // 'canplay' can fire repeatedly on a MediaStream; ReactPlayer's onReady fires once
    const readyFiredRef = useRef(false);
    useEffect(() => {
        if (onReady) {
            const video = videoRef.current;
            const invokeOnReady = () => {
                if (readyFiredRef.current) return;
                readyFiredRef.current = true;
                onReady(thisRef);
            };
            video.addEventListener('canplay', invokeOnReady);
            return () => {
                video.removeEventListener('canplay', invokeOnReady);
            };
        }
    }, [onReady, thisRef]);
    useEffect(() => {
        if (onPlay) {
            const video = videoRef.current;
            video.addEventListener('play', onPlay);
            return () => {
                video.removeEventListener('play', onPlay);
            };
        }
    }, [onPlay]);
    useEffect(() => {
        if (onPause) {
            const video = videoRef.current;
            video.addEventListener('pause', onPause);
            return () => {
                video.removeEventListener('pause', onPause);
            };
        }
    }, [onPause]);
    useEffect(() => {
        if (onBuffer) {
            const video = videoRef.current;
            video.addEventListener('waiting', onBuffer);
            return () => {
                video.removeEventListener('waiting', onBuffer);
            };
        }
    }, [onBuffer]);
    useEffect(() => {
        if (onBufferEnd) {
            const video = videoRef.current;
            video.addEventListener('playing', onBufferEnd);
            return () => {
                video.removeEventListener('playing', onBufferEnd);
            };
        }
    }, [onBufferEnd]);
    useEffect(() => {
        if (onLoadedData) {
            const video = videoRef.current;
            video.addEventListener('loadeddata', onLoadedData);
            return () => {
                video.removeEventListener('loadeddata', onLoadedData);
            };
        }
    }, [onLoadedData]);

    // Ensure video plays and pauses when `playing` prop changes
    useEffect(() => {
        const video = videoRef.current;
        if (!video) return;
        if (playing) {
            // Autoplay may reject if not muted - swallow so we don't crash the tree
            video.play().catch(() => { /* ignore */ });
        } else {
            video.pause();
        }
    }, [playing]);

    // Update video playback rate when prop changes
    useEffect(() => {
        if (playbackRate && videoRef.current) {
            videoRef.current.playbackRate = playbackRate;
        }
    }, [playbackRate]);

    // React sometimes fails to apply the muted attribute to the DOM element,
    // which prevents autoplay - set it manually. Same fix as MpegTsPlayer.
    useEffect(() => {
        const video = videoRef.current;
        if (!video) return;
        video.muted = muted;
        if (!muted) {
            video.removeAttribute('muted');
        } else {
            video.setAttribute('muted', 'muted');
        }
    }, [muted]);

    return (
        // eslint-disable-next-line jsx-a11y/media-has-caption
        <video
            ref={videoRef}
            width={width}
            height={height}
            autoPlay={playing}
            muted={muted}
            playsInline={playsinline}
            // Follow same `config` object style as ReactPlayer / MpegTsPlayer
            {...(config?.file?.attributes ?? {})}
            {...props}
        />
    );
}));

WebRtcPlayer.displayName = 'WebRtcPlayer';

WebRtcPlayer.propTypes = {
    /** WHEP signalling endpoint URL. */
    url: PropTypes.string.isRequired,
    width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    playing: PropTypes.bool,
    muted: PropTypes.bool,
    playsinline: PropTypes.bool,
    playbackRate: PropTypes.number,
    onReady: PropTypes.func,
    onPlay: PropTypes.func,
    onPause: PropTypes.func,
    onBuffer: PropTypes.func,
    onBufferEnd: PropTypes.func,
    onError: PropTypes.func,
    onLoadedData: PropTypes.func,
    /**
     * Reads `config.webrtc` for WebRTC options:
     *   - `headers`: additional headers (or a function returning them) sent with WHEP POST/DELETE,
     *     and re-sent via PATCH whenever the Authorization value changes, to keep the session alive
     *   - `fetch`: optional replacement for global fetch (for full control over auth, etc.)
     *   - `iceServers`: optional STUN/TURN servers (not usually needed when the WHEP server is publicly reachable)
     * And `config.file.attributes` are spread onto the <video> element for parity with the other players.
     */
    config: PropTypes.object
};

export default WebRtcPlayer;
